/**
 * Calls function after delay
 * @param {Function} func Callback function
 * @param {Number} wait Delay in milliseconds
 * @param {Boolean} immediate Whether or not to call immediately
 * @returns {Function} Callback function to call func parameter with delay
 */
function debounce(func, wait, immediate) {
  let timeout;

  /**
   * @returns {void}
   */
  return function callback(...args) {
    const context = this;
    const later = () => {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func.apply(context, args);
  };
}

/**
 * Recursively gets all text from children
 * @param {HTMLElement} node Root node
 * @param {String} txt Existing text
 * @returns {String} Text from children and descendents
 */
function allDescendants(node, txt = '') {
  let text = txt;
  if (!node.childNodes.length) return node.textContent;
  for (let i = 0; i < node.childNodes.length; i += 1) {
    text += node.childNodes[i].textContent;
    const child = node.childNodes[i];
    allDescendants(child, text);
  }
  return text.trim();
}

/**
 * Clears UL nodes from overlay
 * @param {HTMLElement} overlay Overlay element
 * @returns {void}
 */
function clearULNodes(overlay) {
  const ul = overlay.querySelector('ul');
  if (ul.hasChildNodes()) {
    while (ul.firstChild) {
      ul.removeChild(ul.firstChild);
    }
  }
}

/**
 * Callback on input data changing.
 * @param {InputEvent} e Event
 * @param {HTMLElement} inputid Input the data changed on
 * @param {HTMLElement} overlay Overlay element
 * @param {Function<Promise>} source Async function to get source data when input changed
 * @param {Function} processingRows Processes rows
 * @param {Number} minLength Min length to activate autocomplete on
 * @param {Boolean} matchInput Whether or not to match input
 * @param {Function} clickCallBack Callback on click
 * @param {Function} processingRowsDone Process rows done
 * @returns {void}
 */
function inputDataChanged(e, inputid, overlay, source, processingRows, minLength, matchInput, clickCallBack, processingRowsDone) {
  const enteredValue = e.target.value;

  /**
   * Click event listener
   * @param {PointerEvent} ev Event
   * @returns {void}
   */
  function liClickListener(ev) {
    const {
      target
    } = ev;
    let rtnElement = target;
    while (rtnElement.nodeName !== 'LI') {
      rtnElement = rtnElement.parentElement;
    }
    const text = allDescendants(target);
    inputid.value = text; // eslint-disable-line no-param-reassign
    overlay.classList.add('hide');
    if (typeof clickCallBack === 'function') {
      clickCallBack(rtnElement);
    }
  }

  /**
   * Removes nodes and event listeners from element
   * @param {HTMLElement} clearOutNode Node to clear
   * @returns {void}
   */
  function removeNodesAndListeners(clearOutNode) {
    while (clearOutNode.hasChildNodes()) {
      clearOutNode.removeChild(clearOutNode.lastChild);
      clearOutNode.removeEventListener('click', liClickListener, false);
    }
  }

  /**
   * Replaces HTML recursively
   * @param {HTMLElement} node Root element
   * @param {RegExp} regex Regex
   * @returns {void}
   */
  function replaceHtmlRecursivley(node, regex) {
    if (node && !node.hasChildNodes()) {
      if (node.textContent) {
        const text = node.data.replace(regex, elm => `<span class="match-text">${elm}</span>`);
        if (node.parentNode && text.includes('match-text')) {
          node.parentNode.innerHTML = text; // eslint-disable-line no-param-reassign
        }
      }
    } else {
      for (let i = 0; i < node.childNodes.length; i += 1) {
        replaceHtmlRecursivley(node.childNodes[i], regex);
      }
    }
  }
  const clearOutNode = inputid.nextSibling.childNodes[0]; // ul

  if (enteredValue.length >= minLength) {
    source().then(res => {
      // clear it out first
      removeNodesAndListeners(clearOutNode);
      // do a double check to make sure the input length is > 0
      // have noticed the situation where they can enter in a char and then backspace
      // really quickly and the listener is only triggered once
      if (inputid.value.length < 1) return;
      const ul = inputid.nextSibling.childNodes[0]; // ul
      const arr = processingRows(res);
      for (let i = 0; i < arr.length; i += 1) {
        const li = document.createElement('li');
        li.setAttribute('class', 'li-node-row');
        li.setAttribute('role', 'option');
        li.addEventListener('click', liClickListener.bind(li), false);
        const div = document.createElement('div');
        div.setAttribute('class', 'node-row-wrapper');
        const inputText = inputid.value;
        if (matchInput) {
          div.innerHTML = arr[i];
          const searchRegex = new RegExp(inputText, 'gi');
          replaceHtmlRecursivley(div, searchRegex);
        }
        li.appendChild(div);
        ul.appendChild(li);
      }
      overlay.classList.remove('hide');
      if (processingRowsDone) {
        processingRowsDone(res);
      }
    });
  } else {
    removeNodesAndListeners(clearOutNode);
  }
}

/**
 * Adds a change/input listener
 * @param {HTMLElement} inputid Input the data changed on
 * @param {HTMLElement} overlay Overlay element
 * @param {Function<Promise>} source Async function to get source data when input changed
 * @param {Function} processingRows Processes rows
 * @param {Number} minLength Min length to activate autocomplete on
 * @param {Boolean} matchInput Whether or not to match input
 * @param {Number} debounceMs Delay for input in milliseconds.
 * @param {Function} clickCallBack Callback on click
 * @param {Function} processingRowsDone Process rows done
 * @returns {void}
 */
function addChangeListener(inputid, overlay, source, processingRows, minLength, matchInput, debounceMs, clickCallBack, processingRowsDone) {
  inputid.addEventListener('input', debounce(e => {
    inputDataChanged(e, inputid, overlay, source, processingRows, minLength, matchInput, clickCallBack, processingRowsDone);
  }, debounceMs));
}

/**
 * Adds a listener on input keydown
 * @param {HTMLElement} inputid HTML Input to add keydown to
 * @param {HTMLElement} overlay HTML overlay element
 * @param {Function} clickCallBack Callback on click
 * @param {Function} keyDownCallBack Callback on keydown
 * @returns {void}
 */
function addKeyDownListener(inputid, overlay, clickCallBack, keyDownCallBack) {
  inputid.parentNode.setAttribute('role', 'application');
  inputid.addEventListener('keydown', e => {
    if (typeof keyDownCallBack === 'function') {
      keyDownCallBack();
    }
    const keycode = e.which;
    const li = inputid.nextSibling.getElementsByTagName('ul')[0].getElementsByClassName('li-node-row');
    // keydown or keyup
    if (keycode === 38 || keycode === 40) {
      if (overlay.className.includes('hide')) return;
      let currPos = -1;
      for (let i = 0; i < li.length; i += 1) {
        if (li[i].className.includes('row-selected')) {
          currPos = i;
          break;
        }
      }
      if (keycode === 40) {
        currPos += 1;
      } else if (keycode === 38) {
        currPos -= 1;
      }
      if (currPos > -1 && currPos < li.length) {
        li[currPos].classList.add('row-selected');
        const text = li[currPos].innerText;
        const announcementContainer = document.getElementById('announcementContainer');
        if (announcementContainer) {
          announcementContainer.innerText = text;
        }
        for (let i = 0; i < li.length; i += 1) {
          if (i !== currPos) {
            li[i].classList.remove('row-selected');
          }
        }
      } else if (currPos < 0) {
        li[0].classList.remove('row-selected');
      }
      // if scroll box and overflow y
      if (currPos > -1 && currPos < li.length) {
        const heightOverlayBottomY = overlay.getBoundingClientRect().bottom;
        const heightOverlayTopY = overlay.getBoundingClientRect().top;
        const posY = li[currPos].getBoundingClientRect().bottom;
        const buffer = 4;
        if (keycode === 40 && posY > heightOverlayBottomY - buffer) {
          overlay.scrollTop += li[currPos].offsetHeight; // eslint-disable-line no-param-reassign
        } else if (keycode === 38 && heightOverlayTopY + buffer > posY) {
          overlay.scrollTop -= li[currPos].offsetHeight; // eslint-disable-line no-param-reassign
        }
      }
    } else if (keycode === 13) {
      // enter key then select and fill in box
      const node = overlay.querySelector('ul .row-selected');
      if (node !== null) {
        if (node.className.includes('row-selected')) {
          inputid.value = allDescendants(node); // eslint-disable-line no-param-reassign
          overlay.classList.add('hide');
          clearULNodes(overlay);
          node.classList.remove('row-selected');
          if (typeof clickCallBack === 'function') {
            clickCallBack(node);
          }
        }
      }
    }
  });
}

/**
 * Adds a listener on blur of the input
 * @param {HTMLElement} inputid Input element
 * @param {HTMLElement} overlay Overlay element
 * @returns {void}
 */
function addBlurListener(inputid, overlay) {
  inputid.addEventListener('blur', () => {
    setTimeout(() => {
      // remove all children
      clearULNodes(overlay);
      const announcementContainer = document.getElementById('announcementContainer');
      overlay.classList.add('hide');
      if (announcementContainer) {
        announcementContainer.innerText = '';
        return null;
      }
      return false;
    }, 500);
  });
}

/**
 * Adds a focus listener to the given input
 * @param {HTMLElement} inputid Input
 * @param {HTMLElement} overlay Overlay element
 * @param {Function} source Source function
 * @param {Function} processingRows Processes rows
 * @param {Number} minLength Min length to activate autocomplete on
 * @param {Boolean} matchInput Whether or not to match input
 * @param {Function} clickCallBack Callback on click
 * @param {Function} processingRowsDone Process rows done
 * @returns {void}
 */
function addFocusListener(inputid, overlay, source, processingRows, minLength, matchInput, clickCallBack, processingRowsDone) {
  inputid.addEventListener('focus', e => {
    const enteredValue = e.target.value;
    // min length check and only move forward if it's hidden
    if (enteredValue.length >= minLength && overlay.classList && overlay.classList.contains('hide')) {
      inputDataChanged(e, inputid, overlay, source, processingRows, minLength, matchInput, clickCallBack, processingRowsDone);
      overlay.classList.remove('hide');
    }
  });
}

/**
 * Initializes the auto complete utility.
 * @param {String} id HTML element id
 * @param {Object} options Options object
 * @returns {void}
 */
function initAutoComplete(id, options = {}) {
  let {
    minLength,
    matchInput,
    debounceMs,
    adaOptions
  } = options;
  const {
    source,
    processingRows,
    clickCallBack,
    processingRowsDone,
    keyDownCallBack
  } = options;
  const adaLabelDefault = 'Search input. Navigate up and down to hear suggested matches after typing in a partial answer';

  // set defaults
  minLength = minLength || 3;
  matchInput = matchInput || false;
  debounceMs = debounceMs || 0;
  adaOptions = adaOptions || {};
  const adaRole = adaOptions.role || 'application';

  /* If there is no adaInputLabel being passed,
   * then replace it with the field's label name for ADA purposes.
   */
  if (!adaOptions.adaInputLabel) {
    const adaLabelContent = document.querySelector(`label[for=${id}]`).innerHTML;
    const adaLabelName = adaLabelContent.split('<');
    const adaLabelTrimmed = adaLabelName[0].trim();
    adaOptions.adaInputLabel = `${adaLabelTrimmed} ${adaLabelDefault}`;
  }
  const adaInputLabel = adaOptions.adaInputLabel || adaLabelDefault;

  // selectors
  const inputid = document.getElementById(id);
  if (inputid === null) {
    return;
  }
  // some css
  inputid.parentNode.style.position = 'relative';
  // add in absolutely positioned div
  const overlay = document.createElement('div');
  overlay.setAttribute('class', 'overlay-wrapper spa-card--overlay hide');
  overlay.innerHTML = '<ul class="ul-node" role="listbox"></ul>';
  inputid.parentNode.insertBefore(overlay, inputid.nextSibling);
  // set ada attributes
  inputid.setAttribute('aria-label', adaInputLabel);
  overlay.setAttribute('role', adaRole);
  setTimeout(() => {
    let height = inputid.clientHeight;
    let prevSibling = inputid.previousSibling;
    while (prevSibling) {
      height += prevSibling.clientHeight || 0;
      prevSibling = prevSibling.previousSibling;
    }
    overlay.style.top = `${height}px`;
  }, 1000);
  // add listeners
  addKeyDownListener(inputid, overlay, clickCallBack, keyDownCallBack);
  addChangeListener(inputid, overlay, source, processingRows, minLength, matchInput, debounceMs, clickCallBack, processingRowsDone);
  addBlurListener(inputid, overlay);
  addFocusListener(inputid, overlay, source, processingRows, minLength, matchInput, clickCallBack, processingRowsDone);
}
var index = {
  initAutoComplete
};

export { index as default };
